export class MeasureEncoder {
  constructor() {
    this.encoders = {
      day_period_morning: this.dayPeriodEncoder,
      day_period_afternoon: this.dayPeriodEncoder,
      device_type_desktop: this.deviceTypeEncoder,
      referrer_self: this.referrerEncoder,
      referrer_outside: this.referrerEncoder,
      uri_sequence_a: this.uriEncoder,
      uri_sequence_aa: this.uriEncoder,
      uri_sequence_aaa: this.uriEncoder,
      uri_sequence_aaaa: this.uriEncoder,
      uri_sequence_aaab: this.uriEncoder,
      uri_sequence_aab: this.uriEncoder,
      uri_sequence_aaba: this.uriEncoder,
      uri_sequence_aabb: this.uriEncoder,
      uri_sequence_aabc: this.uriEncoder,
      uri_sequence_ab: this.uriEncoder,
      uri_sequence_aba: this.uriEncoder,
      uri_sequence_abaa: this.uriEncoder,
      uri_sequence_abab: this.uriEncoder,
      uri_sequence_abb: this.uriEncoder,
      uri_sequence_abba: this.uriEncoder,
      uri_sequence_abbb: this.uriEncoder,
      uri_sequence_abbc: this.uriEncoder,
      uri_sequence_abc: this.uriEncoder,
      uri_sequence_abca: this.uriEncoder,
      uri_sequence_abcb: this.uriEncoder,
      uri_sequence_abcc: this.uriEncoder,
    }
  }

  encode(measureName, measures) {
    if (Object.keys(this.encoders).includes(measureName)) return this.encoders[measureName](measureName, measures, this)
    else {
      console.error(`[AI] encoder for ${measureName} doesn't exist!`)
      return -999
    }
  }

  referrerEncoder(measureName, measures, self) {
    //check if referrer is in the same domain as url
    const referrer = measures[measures.length - 1]['measures']['referrer']
    const url = measures[measures.length - 1]['url']

    if (referrer.length === 0 || url.length === 0) return -1

    let domainSelf = false
    let domainOutside = false

    try {
      let domain = new URL(url)
      let referrerDomain = new URL(referrer)

      domainSelf = domain.hostname === referrerDomain.hostname
      domainOutside = !domainSelf
    } catch (e) {}

    if (measureName === 'referrer_self') return domainSelf ? 1 : -1
    else return domainOutside ? 1 : -1
  }

  deviceTypeEncoder(measureName, measures, self) {
    const device = measures[measures.length - 1]['measures']['device_type']
    return device === 'desktop' ? 1 : -1
  }

  generateSequence(arr) {
    if (!arr.length) return ''

    let currentChar = 'a'.charCodeAt(0) // Start with 'A'
    let encodingMap = {} // Map to store previously encountered elements and their encodings
    let sequence = ''

    for (let i = 0; i < arr.length; i++) {
      // If the element was seen before, use the previous encoding
      if (encodingMap.hasOwnProperty(arr[i])) {
        sequence += encodingMap[arr[i]]
      } else {
        // Otherwise, add a new encoding for this unique element
        encodingMap[arr[i]] = String.fromCharCode(currentChar)
        sequence += String.fromCharCode(currentChar)
        currentChar++
      }
    }

    return sequence
  }

  uriEncoder(measureName, measures, self) {
    const urls = measures.map((x) => x['url'])
    let sequence = measureName.match(/[^_]+$/)
    return sequence[0] === self.generateSequence(urls) ? 1 : -1
  }

  dayPeriodEncoder(measureName, measures) {
    const dayPeriod = measures[measures.length - 1]['measures']['day_period']

    if (measureName === 'day_period_morning') return dayPeriod === 'morning' ? 1 : -1
    else return dayPeriod === 'afternoon' ? 1 : -1
  }
}

export default class AiService {
  constructor(measuresOrder = [], scalingVectorR = [], scalingVectorU = [], compressMatrix = [[]], decisionTreeBody = '') {
    this.vectorOrder = measuresOrder.map((x) => x.replace('interaction_measures.', ''))
    this.defaultOrder = [
      'total_visit_time',
      'session_duration',
      'page_returns',
      'page_replays',
      'day_period_morning_encode',
      'day_period_afternoon_encode',
      'device_type_desktop_encode',
      'referrer_self_encode',
      'referrer_outside_encode',
      'uri_sequence_a_encode',
      'uri_sequence_aa_encode',
      'uri_sequence_aaa_encode',
      'uri_sequence_aaaa_encode',
      'uri_sequence_aaab_encode',
      'uri_sequence_aab_encode',
      'uri_sequence_aaba_encode',
      'uri_sequence_aabb_encode',
      'uri_sequence_aabc_encode',
      'uri_sequence_ab_encode',
      'uri_sequence_aba_encode',
      'uri_sequence_abaa_encode',
      'uri_sequence_abab_encode',
      'uri_sequence_abb_encode',
      'uri_sequence_abba_encode',
      'uri_sequence_abbb_encode',
      'uri_sequence_abbc_encode',
      'uri_sequence_abc_encode',
      'uri_sequence_abca_encode',
      'uri_sequence_abcb_encode',
      'uri_sequence_abcc_encode',
      'activity_time_interval_t0',
      'activity_time_interval_t1',
      'activity_time_interval_t2',
      'activity_time_interval_t3',
      'activity_time_interval_wa_avgo',
      'idle_time_t0',
      'idle_time_t1',
      'idle_time_t2',
      'idle_time_t3',
      'idle_time_wa_avgo',
      'click_t0',
      'click_t1',
      'click_t2',
      'click_t3',
      'click_wa_sum_dt',
      'click_decision_delay_t0',
      'click_decision_delay_t1',
      'click_decision_delay_t2',
      'click_decision_delay_t3',
      'click_decision_delay_wa_avgo',
      'exit_intent_t0',
      'exit_intent_t1',
      'exit_intent_t2',
      'exit_intent_t3',
      'text_interaction_t0',
      'text_interaction_t1',
      'text_interaction_t2',
      'text_interaction_t3',
      'mouse_movement_t0',
      'mouse_movement_t1',
      'mouse_movement_t2',
      'mouse_movement_t3',
      'mouse_movement_wa_sum_dt',
      'fixation_duration_t0',
      'fixation_duration_t1',
      'fixation_duration_t2',
      'fixation_duration_t3',
      'fixation_duration_wa_sum_dt',
      'fixation_count_t0',
      'fixation_count_t1',
      'fixation_count_t2',
      'fixation_count_t3',
      'scroll_fixation_duration_t0',
      'scroll_fixation_duration_t1',
      'scroll_fixation_duration_t2',
      'scroll_fixation_duration_t3',
      'scroll_fixation_duration_wa_sum_dt',
      'scroll_fixation_count_t0',
      'scroll_fixation_count_t1',
      'scroll_fixation_count_t2',
      'scroll_fixation_count_t3',
      'scroll_distance_t0',
      'scroll_distance_t1',
      'scroll_distance_t2',
      'scroll_distance_t3',
      'scroll_distance_wa_sum_dt',
      'scroll_ratio_max_t0',
      'scroll_ratio_max_t1',
      'scroll_ratio_max_t2',
      'scroll_ratio_max_t3',
      'scroll_ratio_max_wa_max',
      'headsup_show_count',
      'clipboard_count',
      'session_duration_raw',
    ]

    this.R = scalingVectorR
    this.U = scalingVectorU
    this.K = compressMatrix
    this.decisionTreeBody = decisionTreeBody

    this.encoder = new MeasureEncoder()
  }

  processPipeline(measures = []) {
    let measuresVector = this.buildMeasuresVector(measures)
    let scaledVector = this.scaleMeasuresVector(measuresVector, this.U, this.R)
    let compressedVector = this.compressMeasuresVector(scaledVector, this.K)
    return this.runDecisionTree(compressedVector, this.decisionTreeBody)
  }

  buildMeasuresVector(measures) {
    let measuresArray = []
    for (let o in measures) measuresArray.push(measures[o])

    measuresArray.sort(function (a, b) {
      return new Date(b.timestamp) - new Date(a.timestamp)
    })

    let vector = []

    const latestMeasures = measuresArray[0]
    const activityIntervalTimeSum = measuresArray
      .map((x) => {
        return x['measures']['activity_time_interval'] || 0
      })
      .reduce((accumulator, currentValue) => {
        return accumulator + currentValue
      }, 0)

    this.vectorOrder.forEach((key) => {
      if (key.match(/_t\d$/)) {
        //time interval

        const measureName = key.replace(/_t\d$/, '')
        const interval = key.charAt(key.length - 1)

        try {
          let value = measuresArray[interval]['measures'][measureName] || 0
          value = Number(value)

          vector.push(value)
        } catch (e) {
          vector.push(0)
        }
      } else if (key.endsWith('_wa_avgo')) {
        //average value

        const measureName = key.replace('_wa_avgo', '')

        let values = []
        measuresArray.forEach((x) => {
          values.push(x['measures'][measureName] || 0)
        })

        let sum = values.reduce((accumulator, currentValue) => {
          return accumulator + currentValue
        }, 0)
        vector.push(sum / values.length)
      } else if (key.endsWith('_wa_max')) {
        //max value

        const measureName = key.replace('_wa_max', '')

        let values = []
        measuresArray.forEach((x) => {
          values.push(x['measures'][measureName] || 0)
        })

        vector.push(Math.max(...values))
      } else if (key.endsWith('_wa_sum_dt')) {
        //values delta

        const measureName = key.replace('_wa_sum_dt', '')

        const valuesSum = measuresArray
          .map((x) => {
            return x['measures'][measureName] || 0
          })
          .reduce((accumulator, currentValue) => {
            return accumulator + currentValue
          }, 0)

        if (activityIntervalTimeSum === 0) {
          vector.push(0)
        } else {
          vector.push((valuesSum * 60) / activityIntervalTimeSum)
        }
      } else if (key.endsWith('_encode')) {
        //value has to be encoded

        //invoke encoder for measure name
        const measureName = key.replace('_encode', '')

        let encodedValue = this.encoder.encode(measureName, measuresArray)

        vector.push(encodedValue)
      } else if (key.endsWith('_raw')) {
        const measureName = key.replace('_raw', '')
        vector.push(latestMeasures['measures'][measureName] || 0)
      } else {
        //last value

        const measureName = key

        vector.push(latestMeasures['measures'][measureName] || 0)
      }
    })

    return vector
  }

  scaleMeasuresVector(measuresVector, uVector, rVector) {
    const measures = [...measuresVector]

    //first 2 elements - log10
    const measuresToLog = ['total_visit_time', 'session_duration']

    measuresToLog.forEach((measureName) => {
      const measureIndex = measures.indexOf(measureName)
      measures[measureIndex] = Math.log10(measures[measureIndex] + 1)
    })

    //scale
    return measures.map((m, index) => {
      return (m - uVector[index]) * rVector[index]
    })
  }

  compressMeasuresVector(measuresVector, compressMatrix) {
    const measures = [...measuresVector]
    if (measures.length !== compressMatrix[0].length) {
      throw new Error('Number of rows in the matrix should match the vector length.')
    }

    let result = []

    for (let i = 0; i < compressMatrix.length; i++) {
      let sum = 0
      for (let j = 0; j < compressMatrix[0].length; j++) {
        sum += compressMatrix[i][j] * measures[j]
      }
      result.push(sum)
    }

    return result
  }

  runDecisionTree(measuresVector, functionBody) {
    const wrap = (s) => '{ return ' + functionBody + ' };'
    const func = new Function(wrap(functionBody))
    return func.call(null).call(null, measuresVector)
  }
}
